import AddOnService from '@spike/addon-service-model';
import { AppointmentCountRecurrent } from '@spike/appointment-model';
import { NewBookingDiscount } from '@spike/new-booking-model';
import { NewBookingsStatus, recurrentRescheduleThunk, singleRescheduleThunk } from '@spike/new-bookings-action';
import { AppointmentOrderItem, Discount, Order, Orderizable, OrderizableTypes } from '@spike/order-model';
import { OrdersStatus, reloadOrderThunk, saveOrderItemThunk } from '@spike/orders-action';
import { Product } from '@spike/product-model';
import useNonInitialEffect from '@versiondos/hooks';
import ComplementaryItem from 'components/CreateBooking/ExtraItems';
import PetAndServices from 'components/CreateBooking/PetAndServices';
import ConfirmNewBookingDialog from 'components/UI/ConfirmNewBookingDialog';
import { useApiClientWrapper } from 'hooks';
import { cloneDeep } from 'lodash';
import uniq from 'lodash/uniq';
import {
    AddOnDate,
    itemTypes,
    Discount as MultipleBookingDiscount,
    PetServiceWrapper,
    versionPetAndServices
} from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import ConfirmRecurringRescheduleDialog from './UI/ConfirmRecurringRescheduleDialog';

export interface EditOrderItemProps {
    order: Order;
    orderItemId: number;
    recurrentInfo?: AppointmentCountRecurrent;
    hideRemove?: boolean;
    className?: string;
    onClose: () => void;
    onCloseOrderDetail?: () => void;
    onDelete?: () => void;
    onRebook?: (appointmentItem: AppointmentOrderItem) => void;
}

export const convertDiscount = (discount?: Discount): MultipleBookingDiscount => {
    if (discount?.percentage) {
        return {
            type: 'percentage',
            value: Number(discount.percentage)
        };
    } else if (discount?.amount) {
        return {
            type: 'amount',
            value: Number(discount.amount)
        };
    } else {
        return {
            type: 'none',
            value: 0
        };
    }
};

const wrapService = (appointmentItem: AppointmentOrderItem): PetServiceWrapper => ({
    //duration: appointmentItem.appointment.duration,
    beforePrice: Number(appointmentItem.orderItem.originalPrice ?? appointmentItem.orderItem.amount),
    service: appointmentItem.appointment.services[0],
    price: Number(appointmentItem.orderItem.amount),
    durationInMinutes: appointmentItem.appointment.duration.to
        .clone()
        .diff(appointmentItem.appointment.duration.from.clone(), 'minutes')
});

const getComplementaryItemProps = (
    order: Order,
    orderItemId: number
): {
    type?: itemTypes;
    parentId: number;
    petId?: number;
    date?: Moment;
    versionPetAndServices?: versionPetAndServices;
    product?: Product;
    addOn?: AddOnService;
    custom?: string;
    discount?: MultipleBookingDiscount;
    quantity?: number;
    amount?: number;
    staffId?: number;
    itemsUsed?: Array<number>;
    dates?: Array<AddOnDate>;
} => {
    const addOnServiceItem = order.items.addOnServices.find(item => item.orderItem.id === orderItemId);
    const productItem = order.items.products.find(item => item.orderItem.id === orderItemId);
    const customItem = order.items.customs.find(item => item.orderItem.id === orderItemId);

    if (addOnServiceItem) {
        return {
            parentId: addOnServiceItem.pet?.clientId ?? order.client.id!,
            petId: addOnServiceItem.orderItem.petId,
            type: itemTypes.addOn,
            date: addOnServiceItem.orderItem.startedAt?.clone(),
            product: undefined,
            addOn: addOnServiceItem.addOnService,
            custom: undefined,
            staffId: addOnServiceItem.orderItem.staffId,
            quantity: addOnServiceItem.orderItem.quantity,
            amount: Number(addOnServiceItem.orderItem.amount),
            discount: convertDiscount(addOnServiceItem.orderItem.discount)
        };
    } else if (productItem) {
        return {
            parentId: productItem.pet?.clientId ?? order.client.id!,
            petId: productItem.orderItem.petId,
            type: itemTypes.product,
            date: productItem.orderItem.startedAt?.clone(),
            product: productItem.product,
            addOn: undefined,
            custom: undefined,
            staffId: productItem.orderItem.staffId,
            quantity: productItem.orderItem.quantity,
            amount: Number(productItem.orderItem.amount),
            discount: convertDiscount(productItem.orderItem.discount),
            itemsUsed: order.items.products
                .filter(item => item.orderItem.petId === productItem.orderItem.petId)
                .map(item => item.orderItem.orderizable.id!)
                .filter(id => id !== productItem.product.id)
        };
    } else if (customItem) {
        return {
            parentId: customItem.pet?.clientId ?? order.client.id!,
            petId: customItem.orderItem.petId,
            type: itemTypes.custom,
            date: customItem.orderItem.startedAt?.clone(),
            product: undefined,
            addOn: undefined,
            custom: customItem.orderItem.description,
            staffId: customItem.orderItem.staffId,
            quantity: customItem.orderItem.quantity,
            amount: Number(customItem.orderItem.amount),
            discount: convertDiscount(customItem.orderItem.discount),
            itemsUsed: undefined
        };
    } else {
        return {
            parentId: order.client.id!
        };
    }
};

export const getOrderItemDescription = (
    addOnService?: AddOnService,
    product?: Product,
    custom?: string
): string | undefined => {
    if (addOnService) {
        return addOnService.name;
    } else if (product) {
        return product.businessProduct?.name ?? product.name;
    } else {
        return custom;
    }
};

export const getOrderItemDiscount = (discount?: MultipleBookingDiscount): Discount | undefined =>
    discount === undefined || discount.type === 'none'
        ? undefined
        : {
              amount: discount.type === 'amount' ? discount.value?.toString() : undefined,
              percentage: discount.type === 'percentage' ? discount.value : undefined
          };

export const getOrderItemOrderizable = (
    addOnService?: AddOnService,
    product?: Product,
    custom?: string
): Orderizable | undefined => {
    if (addOnService) {
        return {
            type: OrderizableTypes.ADDON_SERVICE,
            id: addOnService.id!
        };
    } else if (product) {
        return {
            type: OrderizableTypes.PRODUCT,
            id: product.businessProduct?.id ?? product.id
        };
    } else if (custom) {
        return {
            type: OrderizableTypes.CUSTOM
        };
    } else {
        return undefined;
    }
};

export const getUsedPetServices = (order: Order, petId: number, serviceId?: number) =>
    uniq(
        order.items.appointments
            .filter(
                item =>
                    item.appointment.pet.id === petId &&
                    (serviceId === undefined || item.appointment.services[0].id !== serviceId)
            )
            .map(item => item.appointment.services[0].id)
    );

export const getUsedPetIds = (order: Order) => uniq(order.items.appointments.map(item => item.appointment.pet.id));

const getItemsUsed = (order: Order, orderItemId: number) => {
    const addOnServiceItem = order.items.addOnServices.find(item => item.orderItem.id === orderItemId);
    return order.items.addOnServices
        .filter(item => item.orderItem.petId === addOnServiceItem?.orderItem.petId)
        .map(item => item.orderItem.orderizable.id!);
};

const getDates = (order: Order, orderItemId: number) => {
    const addOnServiceItem = order.items.addOnServices.find(item => item.orderItem.id === orderItemId);

    return order.items.addOnServices
        .filter(item => item.orderItem.petId === addOnServiceItem?.orderItem.petId)
        .filter(item => item.orderItem.orderizable.id === addOnServiceItem?.orderItem.orderizable.id)
        .map(item => ({
            id: item.orderItem.orderizable.id!,
            date: item.orderItem.startedAt?.format('L') ?? ''
        }));
};

interface SingleRescheduleInformation {
    bookingId: number;
    appointmentId: number;
    date: Moment;
    staffId: number | undefined | null;
    serviceId: number | undefined;
    notes: string | undefined;
    sendNotification: boolean | undefined;
    orderItemPrice: number | undefined;
    dateEnd: Moment | undefined;
    discount: NewBookingDiscount | undefined;
}

interface RecurrentRescheduleInformation {
    appointmentId: number;
    date: Moment;
    staffId: number | undefined | null;
    serviceId: number | undefined;
    frecuencyId: string | undefined;
    notes: string | undefined;
    sendNotification: boolean | undefined;
    orderItemPrice: number | undefined;
    dateEnd: Moment | undefined;
    discount: NewBookingDiscount | undefined;
}

export const EditOrderItem: FunctionComponent<EditOrderItemProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const appointmentItem = useMemo(
        () => props.order.items.appointments.find(item => item.orderItem.id === props.orderItemId),
        [props.order, props.orderItemId]
    );

    const hasASingleAppoinment = useMemo(
        () =>
            props.order.items.appointments.length +
                props.order.items.addOnServices.length +
                props.order.items.products.length +
                props.order.items.customs.length ===
            1,
        [props.order]
    );

    const isNotLastRecurrentAppointment = useMemo(() => {
        const primaryServiceItem = props.order.items.appointments[0];
        return (
            primaryServiceItem.orderItem.id === props.orderItemId &&
            primaryServiceItem.appointment.recurringUuid &&
            primaryServiceItem.appointment.duration.from.clone().isBefore(props.recurrentInfo?.lastDate)
        );
    }, [props.order, props.orderItemId]);

    const [saving, setSaving] = useState(false);

    const [recurrentRescheduleConfirmation, setRecurrentRescheduleConfirmation] = useState<{
        show: boolean;
        recurrentRescheduleInfo?: RecurrentRescheduleInformation;
        singleRescheduleInfo?: SingleRescheduleInformation;
        upcomingAppoinmentsCount?: number;
    }>({ show: false });

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.SaveOrderItemSuccess:
            case OrdersStatus.ReloadSuccess:
                setSaving(false);
                props.onClose();
                break;
            case OrdersStatus.NotFound:
            case OrdersStatus.Error:
                setSaving(false);
                closeRecurrentRescheduleConfirmation();
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SingleRescheduleSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            case NewBookingsStatus.RecurrentRescheduleSuccess:
                props.onCloseOrderDetail && props.onCloseOrderDetail();
                break;
            case NewBookingsStatus.NotFound:
            case NewBookingsStatus.Error:
                closeRecurrentRescheduleConfirmation();
                setSaving(false);
        }
    }, [newBookingsStatus]);

    const usedPetServicesIds: Array<number> = useMemo(
        () => getUsedPetServices(props.order, appointmentItem?.pet.id!, appointmentItem?.appointment.services[0].id),
        [props.order, props.orderItemId]
    );

    const usedPetIds: Array<number> = useMemo(() => getUsedPetIds(props.order), [props.order, props.orderItemId]);

    const complementaryItemProps = useMemo(
        () => getComplementaryItemProps(props.order, props.orderItemId),
        [props.order, props.orderItemId]
    );

    const rescheduleSingleAppointment = (singleRescheduleInfo: SingleRescheduleInformation) => {
        setSaving(true);

        dispatch(
            singleRescheduleThunk(
                apiClientWrapper,
                singleRescheduleInfo.bookingId,
                singleRescheduleInfo.appointmentId,
                singleRescheduleInfo.date,
                singleRescheduleInfo.staffId,
                singleRescheduleInfo.serviceId,
                singleRescheduleInfo.notes,
                singleRescheduleInfo.sendNotification,
                singleRescheduleInfo.orderItemPrice,
                singleRescheduleInfo.dateEnd,
                singleRescheduleInfo.discount,
                true
            )
        );
    };

    const rescheduleRecurrentAppointment = (recurrentRescheduleInfo: RecurrentRescheduleInformation) => {
        setSaving(true);

        dispatch(
            recurrentRescheduleThunk(
                apiClientWrapper,
                recurrentRescheduleInfo.appointmentId,
                recurrentRescheduleInfo.date,
                recurrentRescheduleInfo.staffId,
                recurrentRescheduleInfo.serviceId,
                recurrentRescheduleInfo.frecuencyId,
                recurrentRescheduleInfo.notes,
                recurrentRescheduleInfo.sendNotification,
                recurrentRescheduleInfo.orderItemPrice,
                recurrentRescheduleInfo.dateEnd,
                recurrentRescheduleInfo.discount
            )
        );
    };

    const closeRecurrentRescheduleConfirmation = () => setRecurrentRescheduleConfirmation({ show: false });

    const saveServiceHandler = (
        date: Moment | undefined,
        petId: number | undefined,
        service: PetServiceWrapper | undefined,
        staffId: number | undefined,
        discount: MultipleBookingDiscount | undefined
    ) => {
        const appointment = appointmentItem?.appointment;

        if (appointment) {
            const sameDate = appointment.duration.from.isSame(date, 'date');

            const singleRescheduleInfo: SingleRescheduleInformation = {
                bookingId: appointment.bookingId,
                appointmentId: appointment.id!,
                date: date?.clone() ?? appointment.duration.from.clone(),
                staffId: staffId,
                serviceId: service?.service.id,
                notes: undefined,
                sendNotification: false,
                orderItemPrice: service?.price,
                dateEnd: date?.clone().add(service?.durationInMinutes, 'minutes'),
                discount: getOrderItemDiscount(discount)
            };

            if (isNotLastRecurrentAppointment && hasASingleAppoinment) {
                const recurrentRescheduleInfo: RecurrentRescheduleInformation = {
                    appointmentId: appointment.id!,
                    date: date?.clone() ?? appointment.duration.from.clone(),
                    staffId: staffId,
                    serviceId: service?.service.id,
                    frecuencyId: appointment.recurringFrecuency?.id,
                    notes: undefined,
                    sendNotification: false,
                    orderItemPrice: service?.price,
                    dateEnd: date?.clone().add(service?.durationInMinutes, 'minutes'),
                    discount: getOrderItemDiscount(discount)
                };

                setRecurrentRescheduleConfirmation({
                    show: true,
                    upcomingAppoinmentsCount: props.recurrentInfo?.remaining
                        ? props.recurrentInfo?.remaining - 1
                        : undefined,
                    recurrentRescheduleInfo,
                    singleRescheduleInfo
                });
            } else if (sameDate) {
                rescheduleSingleAppointment(singleRescheduleInfo);
            } else {
                const appointmentItemToRebook = cloneDeep(appointmentItem);

                if (date) {
                    appointmentItemToRebook.appointment.duration.from = date.clone();
                    appointmentItemToRebook.appointment.duration.to = service
                        ? date.clone().add(service.durationInMinutes, 'minutes')
                        : appointmentItemToRebook.appointment.duration.to;
                }
                if (service) {
                    appointmentItemToRebook.appointment.services[0].id = service.service.id;
                    appointmentItemToRebook.appointment.services[0].name = service.service.name;
                    appointmentItemToRebook.orderItem.amount = service.price.toFixed(2);
                }

                if (staffId) {
                    appointmentItemToRebook.orderItem.staffId = staffId;
                }

                if (discount) {
                    appointmentItemToRebook.orderItem.discount = getOrderItemDiscount(discount);
                }

                rescheduleSingleAppointment({
                    ...singleRescheduleInfo,
                    sendNotification: false
                });
            }
        }
    };

    const saveOrderItemHandler = (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: MultipleBookingDiscount,
        quantity?: number,
        amount?: number
    ) => {
        setSaving(true);

        const orderItem = [
            ...props.order.items.addOnServices.map(item => item.orderItem),
            ...props.order.items.products.map(item => item.orderItem),
            ...props.order.items.customs.map(item => item.orderItem)
        ].find(orderItem => orderItem.id === props.orderItemId);

        if (orderItem) {
            const updatedOrderItem = {
                id: orderItem.id,
                uuid: orderItem.uuid,
                orderId: orderItem.orderId,
                orderizable: getOrderItemOrderizable(selectedAddOn, selectedProduct, custom) ?? {
                    ...orderItem.orderizable
                },
                petId: petId,
                staffId: staffId,
                description: getOrderItemDescription(selectedAddOn, selectedProduct, custom) ?? '',
                quantity: quantity ?? orderItem.quantity,
                amount: amount?.toString() ?? orderItem.amount,
                discount: getOrderItemDiscount(discount),
                startedAt: date?.clone()
            };

            dispatch(saveOrderItemThunk(apiClientWrapper, updatedOrderItem));
        }
    };

    return (
        <>
            {appointmentItem ? (
                <PetAndServices
                    parentId={appointmentItem?.appointment.customer.id}
                    petId={appointmentItem?.appointment.pet.id}
                    service={wrapService(appointmentItem)}
                    staffId={appointmentItem.appointment.services[0].staff?.id}
                    date={appointmentItem.appointment.duration.from.clone()}
                    discount={convertDiscount(appointmentItem.orderItem.discount)}
                    versionPetAndServices={versionPetAndServices.edit}
                    petWithoutSelect={true}
                    petsUsed={usedPetIds}
                    servicesUsed={usedPetServicesIds}
                    loading={saving}
                    hideRemove={props.hideRemove}
                    onBack={props.onClose}
                    onBook={saveServiceHandler}
                    onRemove={props.onDelete}
                />
            ) : (
                <ComplementaryItem
                    {...complementaryItemProps}
                    versionPetAndServices={versionPetAndServices.edit}
                    petWithoutSelect={true}
                    loading={saving}
                    itemsUsed={getItemsUsed(props.order, props.orderItemId)}
                    dates={getDates(props.order, props.orderItemId)}
                    onBack={props.onClose}
                    onBook={saveOrderItemHandler}
                    onRemove={props.onDelete}
                />
            )}
            {recurrentRescheduleConfirmation.show && (
                <ConfirmRecurringRescheduleDialog
                    open={true}
                    upcomingAppointments={recurrentRescheduleConfirmation.upcomingAppoinmentsCount ?? 0}
                    onCancel={() => closeRecurrentRescheduleConfirmation()}
                    onUpdate={type => {
                        if (type === 'single') {
                            recurrentRescheduleConfirmation.singleRescheduleInfo
                                ? rescheduleSingleAppointment(recurrentRescheduleConfirmation.singleRescheduleInfo)
                                : closeRecurrentRescheduleConfirmation();
                        } else {
                            recurrentRescheduleConfirmation.recurrentRescheduleInfo
                                ? rescheduleRecurrentAppointment(
                                      recurrentRescheduleConfirmation.recurrentRescheduleInfo
                                  )
                                : closeRecurrentRescheduleConfirmation();
                        }
                    }}
                />
            )}
        </>
    );
};

export default EditOrderItem;
